import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getManagerDetail } from '../api/client';

const getScoreColor = (score: number) => {
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
};

const getLevelBadgeStyle = (level: string): React.CSSProperties => {
  const isGood = level.toLowerCase().includes('эксперт') || level.toLowerCase().includes('профи');
  return {
    display: 'inline-block',
    padding: '4px 14px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    background: isGood ? '#ecfdf5' : '#fef3c7',
    color: isGood ? '#059669' : '#92400e',
    border: isGood ? '1px solid #a7f3d0' : '1px solid #fde68a',
  };
};

const DialogueRow: React.FC<{ d: any; onClick: () => void }> = ({ d, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '14px 16px',
        cursor: 'pointer',
        gap: 16,
        borderRadius: 8,
        transition: 'background 0.15s ease',
        background: hovered ? '#f8fafc' : 'transparent',
        borderBottom: '1px solid #f1f5f9',
      }}
    >
      <div style={{ flex: 1, fontWeight: 500, fontSize: 14, color: '#0f172a' }}>
        {d.client_name || 'Клиент'}
      </div>
      <div style={{
        fontSize: 12, color: '#64748b', background: '#f1f5f9',
        padding: '3px 10px', borderRadius: 20,
      }}>
        {d.channel}
      </div>
      <div style={{
        fontWeight: 600, fontSize: 13, padding: '3px 10px', borderRadius: 20,
        background: d.score >= 80 ? '#ecfdf5' : d.score >= 60 ? '#fef3c7' : '#fef2f2',
        color: d.score >= 80 ? '#059669' : d.score >= 60 ? '#92400e' : '#dc2626',
      }}>
        {d.score ? `${d.score}` : '\u2014'}
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', width: 90, textAlign: 'right' as const }}>
        {new Date(d.date).toLocaleDateString('ru-RU')}
      </div>
    </div>
  );
};

const ManagerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [backHover, setBackHover] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      getManagerDetail(parseInt(id))
        .then((res) => setData(res.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 15 }}>
      Загрузка...
    </div>
  );
  if (!data) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#ef4444', fontSize: 15 }}>
      Менеджер не найден
    </div>
  );

  const scoreColor = getScoreColor(data.current_score);

  return (
    <div style={styles.container}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        onMouseEnter={() => setBackHover(true)}
        onMouseLeave={() => setBackHover(false)}
        style={{
          ...styles.backBtn,
          color: backHover ? '#6366f1' : '#94a3b8',
        }}
      >
        \u2190 Назад к дашборду
      </button>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.name}>{data.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <span style={getLevelBadgeStyle(data.level || '')}>{data.level}</span>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>{data.total_points} очков</span>
          </div>
        </div>
        <div style={{
          fontSize: 48, fontWeight: 700, color: scoreColor,
          letterSpacing: '-1px', lineHeight: 1,
        }}>
          {data.current_score}
          <span style={{ fontSize: 20, color: '#94a3b8', fontWeight: 500 }}>/100</span>
        </div>
      </div>

      {/* Stats Row */}
      <div style={styles.statsRow}>
        {[
          { icon: '\u{1F3AF}', value: data.current_score, label: 'Текущий балл', color: scoreColor },
          { icon: '\u{1F4AC}', value: data.total_dialogues, label: 'Диалогов', color: '#0f172a' },
          { icon: '\u23F1', value: `${data.avg_response_time} мин`, label: 'Ср. время ответа', color: '#0f172a' },
          { icon: '\u{1F4B0}', value: data.upsells_count, label: 'Допродажи', color: '#0f172a' },
        ].map((s, i) => (
          <div key={i} style={styles.statCard}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>{s.icon}</span>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color, marginTop: 8, letterSpacing: '-0.5px' }}>
              {s.value}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Stage Scores */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Оценка по категориям</h3>
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 14 }}>
          {Object.entries(data.stage_scores || {}).map(([stage, score]) => {
            const s = score as number;
            const barColor = getScoreColor(s);
            return (
              <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 160, fontSize: 13, color: '#475569', fontWeight: 500 }}>{stage}</div>
                <div style={{
                  flex: 1, height: 10, background: '#f1f5f9', borderRadius: 5, overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', width: `${s}%`, background: barColor,
                    borderRadius: 5, transition: 'width 0.6s ease',
                  }} />
                </div>
                <div style={{
                  width: 40, fontSize: 14, fontWeight: 700, textAlign: 'right' as const,
                  color: barColor,
                }}>
                  {s}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommendations */}
      {data.recommendations?.length > 0 && (
        <div style={{
          ...styles.card,
          background: 'linear-gradient(135deg, #fefce8 0%, #fff 50%)',
          border: '1px solid #fef08a',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 22 }}>{'\uD83D\uDCA1'}</span>
            <h3 style={{ ...styles.cardTitle, margin: 0 }}>Рекомендации ИИ</h3>
          </div>
          {data.recommendations.map((r: string, i: number) => (
            <div key={i} style={{
              padding: '12px 16px', background: 'rgba(255,255,255,0.7)', borderRadius: 10,
              marginBottom: 8, fontSize: 14, color: '#475569', lineHeight: 1.5,
              border: '1px solid #fef9c3',
            }}>
              {r}
            </div>
          ))}
        </div>
      )}

      {/* Recent Dialogues */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Последние диалоги</h3>
        <div style={{ margin: '0 -4px' }}>
          {data.recent_dialogues?.map((d: any) => (
            <DialogueRow key={d.id} d={d} onClick={() => navigate(`/dialogue/${d.id}`)} />
          ))}
          {(!data.recent_dialogues || data.recent_dialogues.length === 0) && (
            <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
              Нет диалогов
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '32px 36px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: 14,
    marginBottom: 20,
    padding: 0,
    transition: 'color 0.15s ease',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
  },
  name: {
    fontSize: 28,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    marginBottom: 24,
  },
  statCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center' as const,
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: 20,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 16,
    color: '#0f172a',
  },
};

export default ManagerDetail;
